import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth';
import MerchantHeader from '../components/MerchantHeader';
import AIRecommendations from '../components/AIRecommendations';
import { AIBadge } from '../components/AIGlow';

// AI-TRUTH FIX: this page used hardcoded fictional recommendations, a fake "AI analyzed" refresh, fixed
// "Potential Revenue / Avg Confidence / Applied This Week" numbers, and an Apply that silently fell back to
// the first real catalog product before writing a real price. It now only renders the server-sourced
// components/AIRecommendations (honest empty state, named product, price preview, explicit confirmation).
// The previous file is preserved verbatim as commented code at the bottom of this file.
// Restoration check: restore only if (1) recommendations come from the server, (2) no fabricated numbers,
// (3) Apply names the resolved product and resulting price and requires confirmation, (4) no first-product fallback.
export default function AIRecommendationsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const profileData = await authService.getProfile();
        setUser(profileData.member);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load data:', error);
        navigate('/login');
      }
    })();
  }, [navigate]);

  const handleLogout = async () => {
    await authService.logout();
    sessionStorage.clear();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: '#0B1120'}}>
        <p className="text-[#9CB0C7]">Loading AI recommendations...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: '#0B1120'}}>
      <MerchantHeader user={user} onLogout={handleLogout} />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-white">AI Deal Recommendations</h1>
            <AIBadge size="md" pulse={true} />
          </div>
          <p className="text-[#9CB0C7]">Suggestions generated from your own products. Nothing changes until you confirm the product, discount and new price.</p>
        </div>
        <AIRecommendations />
      </main>
    </div>
  );
}

// ===== PRESERVED PREVIOUS VERSION (3595647), do not delete; see docs/CHANGELOG_ai_recommendations_truth.md =====
// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { authService, dashboardService } from '../services/auth';
// import MerchantHeader from '../components/MerchantHeader';
// import AIRecommendations from '../components/AIRecommendations';
// import { AIBadge, AIGlow, AICard } from '../components/AIGlow';
// import { DealGrade } from '../components/DealGrade';
// import { Sparkles, Zap, TrendingUp, Target, RefreshCw } from 'lucide-react';
// import { toast } from 'sonner';
// 
// export default function AIRecommendationsPage() {
//   const navigate = useNavigate();
//   const [user, setUser] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [refreshing, setRefreshing] = useState(false);
//   const [products, setProducts] = useState([]);
// 
//   // AI recommendations state
//   const [recommendations, setRecommendations] = useState([
//     {
//       id: 1,
//       product: 'Premium Wireless Headphones',
//       productId: 'prod-001',
//       currentPrice: 79.99,
//       suggestedPrice: 64.99,
//       discountType: 'percentage',
//       discountValue: 18,
//       predictedCIScore: 875,
//       confidence: 92,
//       reason: 'High demand detected in Electronics category. Similar products seeing 15% increase in conversions.',
//       estimatedRevenue: '+$450/week',
//       urgency: 'high',
//     },
//     {
//       id: 2,
//       product: 'Organic Skincare Set',
//       productId: 'prod-002',
//       currentPrice: 45.99,
//       suggestedPrice: 38.99,
//       discountType: 'percentage',
//       discountValue: 15,
//       predictedCIScore: 780,
//       confidence: 85,
//       reason: 'Seasonal trend analysis shows beauty products peak this month. Competitors have similar offers.',
//       estimatedRevenue: '+$280/week',
//       urgency: 'medium',
//     },
//     {
//       id: 3,
//       product: 'Travel Backpack Pro',
//       productId: 'prod-003',
//       currentPrice: 89.99,
//       suggestedPrice: 74.99,
//       discountType: 'fixed',
//       discountValue: 15,
//       predictedCIScore: 720,
//       confidence: 78,
//       reason: 'Travel season approaching. Bundle with accessories for maximum impact.',
//       estimatedRevenue: '+$320/week',
//       urgency: 'low',
//     },
//   ]);
// 
//   useEffect(() => {
//     loadData();
//   }, []);
// 
//   const loadData = async () => {
//     try {
//       const [profileData, productsData] = await Promise.all([
//         authService.getProfile(),
//         dashboardService.getProducts()
//       ]);
//       setUser(profileData.member);
//       setProducts(productsData.products || []);
//       setLoading(false);
//     } catch (error) {
//       console.error('Failed to load data:', error);
//       navigate('/login');
//     }
//   };
// 
//   const handleLogout = async () => {
//     await authService.logout();
//     sessionStorage.clear();
//     navigate('/login');
//   };
// 
//   const handleRefresh = async () => {
//     setRefreshing(true);
//     // Simulate AI refresh
//     await new Promise(resolve => setTimeout(resolve, 2000));
//     toast.success('Recommendations refreshed!', {
//       description: 'AI has analyzed latest market data',
//     });
//     setRefreshing(false);
//   };
// 
//   const handleApplyRecommendation = async (rec) => {
//     try {
//       // Find matching product by productId OR by title (case-insensitive)
//       let product = null;
//       
//       // First try to match by productId if it's a valid MongoDB ObjectId
//       if (rec.productId && rec.productId.match(/^[0-9a-fA-F]{24}$/)) {
//         product = products.find(p => p._id === rec.productId);
//       }
//       
//       // If not found by ID, try to match by title (case-insensitive, partial match)
//       if (!product && rec.product) {
//         product = products.find(p => 
//           p.title.toLowerCase().includes(rec.product.toLowerCase()) ||
//           rec.product.toLowerCase().includes(p.title.toLowerCase())
//         );
//       }
//       
//       // If still not found, use first product as fallback (for demo purposes)
//       if (!product && products.length > 0) {
//         product = products[0];
//         toast.info('Using first available product as demo', {
//           description: `Recommendation was for "${rec.product}" but using "${product.title}"`,
//         });
//       }
//       
//       if (!product) {
//         toast.error('No products found', {
//           description: 'Please add products first before applying recommendations',
//         });
//         return;
//       }
// 
//       // Create deal with the found product
//       const dealResponse = await dashboardService.createDeal({
//         product_id: product._id,
//         discount_type: rec.discountType,
//         discount_value: rec.discountValue,
//         coupon_code: null,
//         end_date: null
//       });
// 
//       toast.success('Deal created successfully!', {
//         description: `Applied ${rec.discountValue}${rec.discountType === 'percentage' ? '%' : '$'} discount to ${product.title}`,
//       });
// 
//       // Remove from recommendations
//       setRecommendations(prev => prev.filter(r => r.id !== rec.id));
//       
//     } catch (error) {
//       console.error('Failed to apply recommendation:', error);
//       
//       // Better error messaging
//       if (error.response?.status === 403) {
//         const detail = error.response?.data?.detail;
//         toast.error('Subscription limit reached', {
//           description: detail?.message || 'Upgrade your plan to create more deals',
//         });
//       } else if (error.response?.status === 404) {
//         toast.error('Product not found', {
//           description: 'The product for this recommendation may have been deleted',
//         });
//       } else {
//         toast.error('Failed to create deal', {
//           description: error.message || 'Please try again or contact support',
//         });
//       }
//     }
//   };
// 
//   const handleDismiss = (recId) => {
//     setRecommendations(prev => prev.filter(r => r.id !== recId));
//     toast.info('Recommendation dismissed');
//   };
// 
//   if (loading) {
//     return (
//       <div className="min-h-screen flex items-center justify-center" style={{background: '#0B1120'}}>
//         <div className="text-center">
//           <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
//             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//           </svg>
//           <p className="text-[#9CB0C7]">Loading AI recommendations...</p>
//         </div>
//       </div>
//     );
//   }
// 
//   return (
//     <div className="min-h-screen" style={{background: '#0B1120'}}>
//       <MerchantHeader user={user} onLogout={handleLogout} />
// 
//       <main className="max-w-7xl mx-auto px-6 py-8">
//         {/* Header */}
//         <div className="flex items-center justify-between mb-8">
//           <div>
//             <div className="flex items-center gap-3 mb-2">
//               <h1 className="text-3xl font-bold text-white">AI Deal Recommendations</h1>
//               <AIBadge size="md" pulse={true} />
//             </div>
//             <p className="text-[#9CB0C7]">AI-powered suggestions to maximize your revenue and CI Scores</p>
//           </div>
//           <button
//             onClick={handleRefresh}
//             disabled={refreshing}
//             className="px-4 py-2 rounded-lg bg-[#7A5CFF] text-white flex items-center gap-2 hover:bg-[#7A5CFF]/90 disabled:opacity-50"
//           >
//             <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
//             {refreshing ? 'Refreshing...' : 'Refresh'}
//           </button>
//         </div>
// 
//         {/* AI Summary Stats */}
//         <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
//           <AICard className="p-6">
//             <div className="flex items-center justify-between mb-2">
//               <p className="text-sm text-[#9CB0C7]">Active Recommendations</p>
//               <Sparkles className="w-5 h-5 text-[#7A5CFF]" />
//             </div>
//             <p className="text-3xl font-bold text-white">{recommendations.length}</p>
//           </AICard>
// 
//           <AICard className="p-6">
//             <div className="flex items-center justify-between mb-2">
//               <p className="text-sm text-[#9CB0C7]">Potential Revenue</p>
//               <TrendingUp className="w-5 h-5 text-[#8CF78E]" />
//             </div>
//             <p className="text-3xl font-bold text-[#8CF78E]">+$1,050/wk</p>
//           </AICard>
// 
//           <AICard className="p-6">
//             <div className="flex items-center justify-between mb-2">
//               <p className="text-sm text-[#9CB0C7]">Avg Confidence</p>
//               <Target className="w-5 h-5 text-[#3EF1C7]" />
//             </div>
//             <p className="text-3xl font-bold text-white">85%</p>
//           </AICard>
// 
//           <AICard className="p-6">
//             <div className="flex items-center justify-between mb-2">
//               <p className="text-sm text-[#9CB0C7]">Applied This Week</p>
//               <Zap className="w-5 h-5 text-[#FFC857]" />
//             </div>
//             <p className="text-3xl font-bold text-white">12</p>
//           </AICard>
//         </div>
// 
//         {/* Recommendations List */}
//         <div className="space-y-6">
//           {recommendations.length === 0 ? (
//             <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 p-12 text-center">
//               <Sparkles className="w-12 h-12 text-[#7A5CFF] mx-auto mb-4" />
//               <h3 className="text-lg font-semibold text-white mb-2">No recommendations right now</h3>
//               <p className="text-[#9CB0C7]">Our AI is analyzing your products. Check back soon!</p>
//             </div>
//           ) : (
//             recommendations.map((rec) => (
//               <div
//                 key={rec.id}
//                 className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur overflow-hidden"
//               >
//                 <div className={`h-1 ${
//                   rec.urgency === 'high' ? 'bg-gradient-to-r from-[#F55454] to-[#FFC857]' :
//                   rec.urgency === 'medium' ? 'bg-gradient-to-r from-[#FFC857] to-[#3EF1C7]' :
//                   'bg-gradient-to-r from-[#3EF1C7] to-[#7A5CFF]'
//                 }`} />
//                 
//                 <div className="p-6">
//                   <div className="flex items-start justify-between mb-4">
//                     <div>
//                       <div className="flex items-center gap-3 mb-2">
//                         <h3 className="text-lg font-semibold text-white">{rec.product}</h3>
//                         <span className={`px-2 py-0.5 text-xs rounded uppercase font-medium ${
//                           rec.urgency === 'high' ? 'bg-[#F55454]/10 text-[#F55454]' :
//                           rec.urgency === 'medium' ? 'bg-[#FFC857]/10 text-[#FFC857]' :
//                           'bg-[#3EF1C7]/10 text-[#3EF1C7]'
//                         }`}>
//                           {rec.urgency} priority
//                         </span>
//                       </div>
//                       <p className="text-[#9CB0C7]">{rec.reason}</p>
//                     </div>
//                     <div className="flex items-center gap-2">
//                       <DealGrade score={rec.predictedCIScore} showLabel={true} />
//                     </div>
//                   </div>
// 
//                   <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
//                     <div className="p-3 rounded-lg bg-[#111A2E]">
//                       <p className="text-xs text-[#9CB0C7] mb-1">Current Price</p>
//                       <p className="text-lg font-bold text-white">${rec.currentPrice}</p>
//                     </div>
//                     <div className="p-3 rounded-lg bg-[#111A2E]">
//                       <p className="text-xs text-[#9CB0C7] mb-1">Suggested Price</p>
//                       <p className="text-lg font-bold text-[#8CF78E]">${rec.suggestedPrice}</p>
//                     </div>
//                     <div className="p-3 rounded-lg bg-[#111A2E]">
//                       <p className="text-xs text-[#9CB0C7] mb-1">Discount</p>
//                       <p className="text-lg font-bold text-[#3EF1C7]">
//                         {rec.discountType === 'percentage' ? `${rec.discountValue}%` : `$${rec.discountValue}`}
//                       </p>
//                     </div>
//                     <div className="p-3 rounded-lg bg-[#111A2E]">
//                       <p className="text-xs text-[#9CB0C7] mb-1">AI Confidence</p>
//                       <p className="text-lg font-bold text-[#7A5CFF]">{rec.confidence}%</p>
//                     </div>
//                     <div className="p-3 rounded-lg bg-[#111A2E]">
//                       <p className="text-xs text-[#9CB0C7] mb-1">Est. Revenue</p>
//                       <p className="text-lg font-bold text-[#8CF78E]">{rec.estimatedRevenue}</p>
//                     </div>
//                   </div>
// 
//                   <div className="flex gap-3">
//                     <button
//                       onClick={() => handleApplyRecommendation(rec)}
//                       className="px-6 py-2 rounded-lg bg-gradient-to-r from-[#3EF1C7] to-[#7A5CFF] text-white font-semibold hover:opacity-90 transition-opacity flex items-center gap-2"
//                     >
//                       <Zap className="w-4 h-4" />
//                       Apply Recommendation
//                     </button>
//                     <button
//                       onClick={() => handleDismiss(rec.id)}
//                       className="px-6 py-2 rounded-lg border border-[#22304A] text-[#9CB0C7] hover:bg-[#1A2437] transition-colors"
//                     >
//                       Dismiss
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             ))
//           )}
//         </div>
//       </main>
//     </div>
//   );
// }
